/**
 * Competing submissions for one play, side by side (AC-ARCH-004).
 *
 * Renders as an inline panel beside the table on a wide screen and as a
 * full-height overlay on a phone — the archive decides which, this component
 * only renders the content.
 *
 * Every version card carries the four things a resolver needs to judge it:
 * WHO submitted it (name + role), WHEN, WHICH fields it disagrees with the
 * active version on, and — on demand — the whole stat payload. The diff colours
 * are the spec's: green for a value this version supplies and the active one
 * does not, red for one the active version has and this one drops, yellow for a
 * straight disagreement.
 *
 * The "Resolve conflict" button is rendered from the server's own
 * `can_resolve` verdict, so a General Stat Operator never sees a control that
 * would answer 403 (AC-PLAY-VER-002).
 */
import { useState } from 'react';

import type { PlayVersionPage, PlayVersionView } from '../../lib/api-types';
import { isPersona, PERSONA_LABELS } from '../../lib/personas';

export type DiffKind = 'added' | 'removed' | 'changed';

const DIFF_LABELS: Record<DiffKind, string> = {
  added: 'added in this version',
  removed: 'missing from this version',
  changed: 'changed',
};

function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  return Array.isArray(value) && value.length === 0;
}

/** Classify one `diff_vs_active` entry into the spec's three colours. */
export function diffKind(entry: { active: unknown; version: unknown }): DiffKind {
  if (isEmptyValue(entry.active) && !isEmptyValue(entry.version)) return 'added';
  if (!isEmptyValue(entry.active) && isEmptyValue(entry.version)) return 'removed';
  return 'changed';
}

/** `off_personnel` → `Off personnel`. Shared by the archive columns and forms. */
export function statFieldLabel(field: string): string {
  const words = field.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Render any stat value as text a coach can read. Never `[object Object]`. */
export function formatStatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length === 0 ? '—' : value.map(String).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** Role string from the wire → the platform's label, unknown roles unchanged. */
export function roleLabel(role: string): string {
  return isPersona(role) ? PERSONA_LABELS[role] : statFieldLabel(role);
}

/** `2025-09-15T23:04:00Z` → a local, human timestamp. Falls back to the raw string. */
export function formatTimestamp(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function shortId(id: string): string {
  return id.length <= 8 ? id : `${id.slice(0, 8)}…`;
}

function VersionCard({
  version,
  submitterName,
}: {
  version: PlayVersionView;
  submitterName: string;
}): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const diffEntries = Object.entries(version.diff_vs_active);
  const payloadEntries = Object.entries(version.stat_payload);

  return (
    <li
      className={`version-card${version.is_active ? ' version-card--active' : ''}`}
      data-testid={`version-card-${version.version_id}`}
    >
      <header className="version-card__head">
        <span className="version-card__who">{submitterName}</span>
        <span className="badge version-card__role">{roleLabel(version.submitted_by_role)}</span>
        {version.is_active ? <span className="badge badge--brand">Active</span> : null}
      </header>
      <p className="version-card__meta muted">
        <span className="mono">{shortId(version.version_id)}</span>
        {' · submitted '}
        <time dateTime={version.submitted_at}>{formatTimestamp(version.submitted_at)}</time>
      </p>

      {version.is_active ? (
        <p className="version-card__diff-empty muted">
          This is the version the archive currently shows.
        </p>
      ) : diffEntries.length === 0 ? (
        <p className="version-card__diff-empty muted">Identical to the active version.</p>
      ) : (
        <dl className="version-diff">
          {diffEntries.map(([field, entry]) => {
            const kind = diffKind(entry);
            return (
              <div
                key={field}
                className={`version-diff__row version-diff__row--${kind}`}
                data-diff={kind}
                data-testid={`diff-${field}`}
              >
                <dt className="version-diff__field">
                  {statFieldLabel(field)}
                  <span className="visually-hidden"> ({DIFF_LABELS[kind]})</span>
                </dt>
                <dd className="version-diff__values">
                  <span className="version-diff__active">{formatStatValue(entry.active)}</span>
                  <span aria-hidden="true"> → </span>
                  <span className="version-diff__proposed">{formatStatValue(entry.version)}</span>
                </dd>
              </div>
            );
          })}
        </dl>
      )}

      <button
        type="button"
        className="btn btn--ghost btn--sm"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        {isOpen ? 'Hide full stat payload' : `Show full stat payload (${payloadEntries.length})`}
      </button>
      {isOpen ? (
        <dl className="version-card__payload">
          {payloadEntries.map(([field, value]) => (
            <div key={field}>
              <dt>{statFieldLabel(field)}</dt>
              <dd>{formatStatValue(value)}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </li>
  );
}

export interface VersionsPanelProps {
  playNumber: number;
  versions: PlayVersionPage | null;
  /** `user_id` → display name, so a card can say "Dana Ruiz" not a UUID. */
  submitterNames?: Readonly<Record<string, string>>;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  onResolve: () => void;
  onDismiss: () => void;
}

export function VersionsPanel({
  playNumber,
  versions,
  submitterNames = {},
  isLoading,
  error,
  onRetry,
  onResolve,
  onDismiss,
}: VersionsPanelProps): JSX.Element {
  const items = versions?.items ?? [];
  const hasConflict = versions?.conflict_status === 'unresolved';

  return (
    <section className="versions-panel" aria-label={`Versions of play ${playNumber}`}>
      <header className="versions-panel__head">
        <div>
          <h3 className="versions-panel__title">Play #{playNumber} versions</h3>
          <p className="muted">
            {isLoading
              ? 'Loading submissions…'
              : `${items.length} submission${items.length === 1 ? '' : 's'} on record`}
          </p>
        </div>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={onDismiss}
          aria-label="Dismiss versions panel"
        >
          ×
        </button>
      </header>

      {error !== null ? (
        <div className="banner banner--error" role="alert">
          <p className="banner__title">We couldn’t load the versions for this play</p>
          <p>{error}</p>
          <button type="button" className="btn btn--secondary btn--sm" onClick={onRetry}>
            Try again
          </button>
        </div>
      ) : null}

      {isLoading ? (
        <p role="status" className="muted">
          Loading submissions…
        </p>
      ) : null}

      {!isLoading && error === null && items.length === 0 ? (
        <p className="muted">No submissions are recorded for this play.</p>
      ) : null}

      {hasConflict && versions !== null && versions.differing_fields.length > 0 ? (
        <div className="banner banner--warning" role="status">
          <p className="banner__title">Unresolved conflict</p>
          <p>
            Submissions disagree on{' '}
            {versions.differing_fields.map(statFieldLabel).join(', ').toLowerCase()}.
          </p>
        </div>
      ) : null}

      <ul className="versions-panel__list">
        {items.map((version) => (
          <VersionCard
            key={version.version_id}
            version={version}
            submitterName={
              (version.submitted_by === null ? null : submitterNames[version.submitted_by]) ??
              'Unknown operator'
            }
          />
        ))}
      </ul>

      {versions?.can_resolve === true && hasConflict ? (
        <div className="btn-row versions-panel__actions">
          <button type="button" className="btn btn--primary" onClick={onResolve}>
            Resolve conflict
          </button>
        </div>
      ) : null}
      {versions !== null && !versions.can_resolve && hasConflict ? (
        <p className="muted versions-panel__readonly">
          Only a Primary Stat Operator or Team Admin can resolve this conflict.
        </p>
      ) : null}
    </section>
  );
}
